import {useCallback} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {PropertyListApplicationButtonVariantBCD} from "../../property_list/components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    onLeadBtnClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {property} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        props.onClick && props.onClick(property);
    }, [property]);

    const isReserved = property.is_reserved;
    const isPromotion = property.is_promo;

    const Component = props.as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={[p(2), propertyBox]}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target}>
                <FavoriteButton offer={offer} property={property} css={subscribeButton} display={{type: "icon"}} />
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                    loading="lazy"
                />

                <div css={[mt(3), mb(2), contentWrap]}>
                    <ul css={[propertyBoxInfoList]}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <div css={propertyButton}>
                    <PropertyListApplicationButtonVariantBCD
                        property={property}
                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                        vendorId={offer.vendor.id}
                        isPlansList
                        onLeadBtnClick={props.onLeadBtnClick}
                    />
                </div>
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const propertyButton = css`
    flex: 0 1 auto;
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const lightColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;

const badgeWrapper = css`
    position: absolute;
    z-index: ${badgeLayerIndex};
    display: flex;
    gap: 1rem;
`;

const subscribeButton = css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${badgeLayerIndex};
`;
import {useCallback} from "react";
import {css, Theme} from "@emotion/react";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {mb, mr, mt, p} from "@pg-design/helpers-css";
import {LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";

import {getPropertyPlanAltText} from "../../comparison_tool/utils/get_property_plan_modal_title";
import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPrice} from "../../hooks/use_should_show_price";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {PropertyListApplicationButtonVariantBCD} from "../../property_list/components/property_list_application_button/PropertyListApplicationButtonVariantBCD";
import {getCurrency} from "../../utils/get_currency";
import {OfferBoxBase} from "../OfferBoxBase";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    onLeadBtnClick?: (property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {property} = props;
    const {offer} = property;

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const planImage = externalPlanUrl || head(property.plan_image_pages)?.image.p_img_375x250;
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPrice({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        props.onClick && props.onClick(property);
    }, [property]);

    const isReserved = property.is_reserved;
    const isPromotion = property.is_promo;

    const Component = props.as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} css={[p(2), propertyBox]}>
            <Component href={propertyLink} css={propertyBoxLink} title={offer.name} target={target}>
                <FavoriteButton offer={offer} property={property} css={subscribeButton} display={{type: "icon"}} />
                <div css={badgeWrapper}>
                    {isReserved && (
                        <Badge variant="label_danger" icon={() => <LockOutlineIcon size="1.6" fill="white" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {!isReserved && isPromotion && (
                        <Badge variant="label_info" icon={() => <PercentIcon size="1.6" css={mr()} />}>
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                </div>

                <CenteredImage
                    css={planWrap}
                    src={planImage}
                    alt={altText}
                    width={375}
                    height={250}
                    breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                    loading="lazy"
                />

                <div css={[mt(3), mb(2), contentWrap]}>
                    <ul css={[propertyBoxInfoList]}>
                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {property.rooms}
                            </Text>
                        </li>

                        <li css={propertyBoxInfoListItem}>
                            <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span">
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li css={propertyBoxInfoListItem}>
                                <Text css={[lightColor, mb(0.5)]} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {unit: getCurrency({currency: offer.currency})})}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <div css={propertyButton}>
                    <PropertyListApplicationButtonVariantBCD
                        property={property}
                        isAutoresponderEnabled={offer.configuration.is_autoresponder_enabled}
                        vendorId={offer.vendor.id}
                        isPlansList
                        onLeadBtnClick={props.onLeadBtnClick}
                    />
                </div>
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;

    &:hover,
    &:focus {
        color: inherit;
        text-decoration: none;
    }
`;

const propertyBox = css`
    height: 100%;
`;

const planWrap = css`
    flex: 0 0 auto;

    & img {
        object-fit: contain;
        width: 100% !important; // override CenteredImage styles to fit image to container
        height: 100% !important; // override CenteredImage styles to fit image to container
    }
`;

const contentWrap = css`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
`;

const propertyButton = css`
    flex: 0 1 auto;
`;

const propertyBoxInfoList = css`
    padding-left: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const propertyBoxInfoListItem = css`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const lightColor = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
`;

const badgeWrapper = css`
    position: absolute;
    z-index: ${badgeLayerIndex};
    display: flex;
    gap: 1rem;
`;

const subscribeButton = css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: ${badgeLayerIndex};
`;
